import { useEffect, useState } from "react";

import { AdminButton } from "@/components/admin/button";
import { Card } from "@/components/admin/card";
import { AdminIcon } from "@/components/admin/icon";
import { PageHead } from "@/components/admin/page-head";
import {
	useLaunchSubscribers,
	useSiteConfig,
	useUpdateSiteConfig,
} from "@/hooks/use-site";

function toLocalInput(iso?: string): string {
	if (!iso) return "";
	const d = new Date(iso);
	// valeur attendue par <input type="datetime-local"> : YYYY-MM-DDTHH:mm
	const off = d.getTimezoneOffset();
	return new Date(d.getTime() - off * 60000).toISOString().slice(0, 16);
}

export function AdminSiteLaunchPage() {
	const { data: cfg, isLoading } = useSiteConfig();
	const update = useUpdateSiteConfig();
	const { data: subs } = useLaunchSubscribers();

	const [launchDate, setLaunchDate] = useState("");
	useEffect(() => {
		setLaunchDate(toLocalInput(cfg?.launchDate));
	}, [cfg?.launchDate]);

	const comingSoon = cfg?.comingSoonEnabled ?? false;

	const toggleComingSoon = () => {
		if (!cfg) return;
		update.mutate({ comingSoonEnabled: !comingSoon });
	};

	const saveLaunchDate = () => {
		if (!launchDate) return;
		update.mutate({ launchDate: new Date(launchDate).toISOString() });
	};

	const exportCsv = () => {
		if (!subs?.items?.length) return;
		const rows = [
			["email", "source", "date"],
			...subs.items.map((s) => [
				s.email,
				s.source,
				new Date(s.createdAt).toISOString(),
			]),
		];
		const csv = rows
			.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","))
			.join("\n");
		const url = URL.createObjectURL(
			new Blob([csv], { type: "text/csv;charset=utf-8" }),
		);
		const a = document.createElement("a");
		a.href = url;
		a.download = `akwaba-inscrits-lancement-${new Date().toISOString().slice(0, 10)}.csv`;
		a.click();
		URL.revokeObjectURL(url);
	};

	return (
		<>
			<PageHead
				title="Lancement / Site"
				sub="Page « Coming Soon » du storefront public et inscrits au lancement"
				actions={
					<AdminButton variant="ghost" onClick={exportCsv}>
						<AdminIcon n="download" s={14} />
						Exporter les inscrits
					</AdminButton>
				}
			/>

			<div className="kpi-grid">
				<div className={`kpi ${comingSoon ? "accent" : "success"}`}>
					<div className="kpi-l">Mode Coming Soon</div>
					<div className="kpi-v">
						{isLoading ? "…" : comingSoon ? "ACTIVÉ" : "DÉSACTIVÉ"}
					</div>
					<span className="kpi-delta neutral">
						{comingSoon
							? "Le storefront est masqué"
							: "Le storefront est public"}
					</span>
				</div>
				<div className="kpi">
					<div className="kpi-l">Inscrits au lancement</div>
					<div className="kpi-v tn">{subs?.count ?? 0}</div>
					<span className="kpi-delta up">via la page Coming Soon</span>
				</div>
				<div className="kpi forest">
					<div className="kpi-l">Date de lancement</div>
					<div className="kpi-v" style={{ fontSize: 18 }}>
						{cfg?.launchDate
							? new Date(cfg.launchDate).toLocaleString("fr-FR")
							: "—"}
					</div>
				</div>
			</div>

			<Card>
				<div
					style={{
						padding: 20,
						display: "flex",
						flexDirection: "column",
						gap: 24,
					}}
				>
					{/* Toggle coming-soon */}
					<div
						style={{
							display: "flex",
							alignItems: "center",
							justifyContent: "space-between",
							gap: 16,
							flexWrap: "wrap",
						}}
					>
						<div>
							<div style={{ fontWeight: 700, fontSize: 15 }}>
								Page « Coming Soon »
							</div>
							<div
								style={{
									fontSize: 13,
									opacity: 0.7,
									marginTop: 4,
									maxWidth: 540,
								}}
							>
								Quand c'est activé, tous les visiteurs du storefront voient la
								page d'attente (compte à rebours + inscription email) au lieu de
								la boutique. Désactive pour ouvrir la boutique au public le jour
								du lancement.
							</div>
						</div>
						<button
							type="button"
							onClick={toggleComingSoon}
							disabled={update.isPending || isLoading}
							aria-pressed={comingSoon}
							style={{
								cursor: "pointer",
								width: 64,
								height: 34,
								borderRadius: 999,
								border: "none",
								background: comingSoon ? "#C6563A" : "#cbd5e1",
								position: "relative",
								transition: "background .15s",
								flexShrink: 0,
							}}
						>
							<span
								style={{
									position: "absolute",
									top: 4,
									left: comingSoon ? 34 : 4,
									width: 26,
									height: 26,
									borderRadius: "50%",
									background: "#fff",
									transition: "left .15s",
								}}
							/>
						</button>
					</div>

					{/* Launch date */}
					<div
						style={{
							borderTop: "1px solid var(--border, #e5e7eb)",
							paddingTop: 20,
						}}
					>
						<div style={{ fontWeight: 700, fontSize: 15, marginBottom: 8 }}>
							Date de lancement
						</div>
						<div
							style={{
								display: "flex",
								gap: 10,
								alignItems: "center",
								flexWrap: "wrap",
							}}
						>
							<input
								type="datetime-local"
								value={launchDate}
								onChange={(e) => setLaunchDate(e.target.value)}
								style={{
									padding: "10px 12px",
									borderRadius: 8,
									border: "1px solid var(--border, #cbd5e1)",
									fontSize: 14,
								}}
							/>
							<AdminButton
								variant="primary"
								onClick={saveLaunchDate}
								disabled={update.isPending}
							>
								{update.isPending ? "Enregistrement…" : "Enregistrer"}
							</AdminButton>
						</div>
						<div style={{ fontSize: 12.5, opacity: 0.6, marginTop: 8 }}>
							Aperçu avant lancement : ouvre le storefront avec{" "}
							<code>?preview=&lt;token&gt;</code> (token{" "}
							<code>AKWABA_PREVIEW_TOKEN</code> côté infra) pour contourner le
							coming-soon.
						</div>
					</div>
				</div>
			</Card>

			<Card>
				<div className="table-wrap" style={{ margin: 0 }}>
					<table className="table">
						<thead>
							<tr>
								<th>Email</th>
								<th>Source</th>
								<th>Inscrit le</th>
							</tr>
						</thead>
						<tbody>
							{(subs?.items ?? []).slice(0, 200).map((s) => (
								<tr key={s.id}>
									<td>{s.email}</td>
									<td>{s.source}</td>
									<td>{new Date(s.createdAt).toLocaleString("fr-FR")}</td>
								</tr>
							))}
							{!subs?.items?.length && (
								<tr>
									<td
										colSpan={3}
										style={{ textAlign: "center", padding: 24, opacity: 0.6 }}
									>
										Aucun inscrit pour le moment.
									</td>
								</tr>
							)}
						</tbody>
					</table>
				</div>
			</Card>
		</>
	);
}
